import { RestrictedAnalytesLookup, Search, SearchLookupData, SearchResult, SortOrder } from '@app/core/models';
import { createFeatureSelector, createReducer, on } from '@ngrx/store';
import { sortObjects } from '../../utils/functional';
import { ASC, DESC } from '../../utils/sorting';
import {
  loadFTPSavedSearch, loadFTPSavedSearchesSuccess, loadFTPSearchLookupDataSuccess,
  loadGlobalLookupDataSuccess, loadRestrictedAnalytesLookupSuccess, loadSavedSearch, loadSavedSearchesSuccess, loadSearchLookupDataSuccess, loadSearchResultsSuccess,
  onScheduleSavedSearchSuccess,
  setFTPSavedSearchesSortOrder, setSavedSearchesSortOrder, setSearchResultsSortOrder, unloadFTPSearchLookupData,
  unloadGlobalLookupData, unloadSavedSearch, unloadSavedSearches, unloadSearchLookupData
} from '../actions/results.actions';

export interface ResultsState {
  searchLookupData?: SearchLookupData;
  searchResults?: SearchResult[];
  searchResultConfirmedForDeletion?: SearchResult;
  searchResultToDelete?: SearchResult;
  savedSearch?: Search;
  savedSearches?: Search[];
  savedSearchConfirmedForDeletion?: Search;
  savedSearchToDelete?: Search;
  savedFTPSearchesSortOrder?: SortOrder;
  savedSearchesSortOrder?: SortOrder;
  searchResultsSortOrder?: SortOrder;
  restrictedAnalytesLookup?: RestrictedAnalytesLookup[];
  sensitiveAccessionsInd?: SearchResult;
}

const initialState: ResultsState = {
  searchLookupData: null,
  savedSearch: null,
  savedSearches: null,
  savedSearchConfirmedForDeletion: null,
  savedSearchToDelete: null,
  searchResults: null,
  searchResultConfirmedForDeletion: null,
  searchResultToDelete: null,
  savedFTPSearchesSortOrder: {
    key: 'lastRunStatusSort',
    direction: ASC
  },
  savedSearchesSortOrder: {
    key: 'createdDate',
    direction: DESC
  },
  searchResultsSortOrder: {
    key: 'submittedTime',
    direction: DESC
  },
  restrictedAnalytesLookup: null,
  sensitiveAccessionsInd: null,

};

export const resultsReducer = createReducer(
  initialState,
  on(loadSavedSearchesSuccess, (state, action) => {
    const savedSearches = sortObjects(action.payload, state.savedSearchesSortOrder);
    return Object.assign({}, state, {
      savedSearches,
    } as ResultsState);
  }),
  on(loadFTPSavedSearchesSuccess, (state, action) => {
    const savedSearches = sortObjects(action.payload, state.savedFTPSearchesSortOrder);

    return Object.assign({}, state, {
      savedSearches,
    } as ResultsState);
  }),
  on(loadSearchResultsSuccess, (state, action) => ({ ...state, searchResults: sortObjects(action.payload, state.searchResultsSortOrder) })),
  on(loadGlobalLookupDataSuccess, (state, action) => ({ ...state, searchLookupData: action.payload })),
  on(unloadGlobalLookupData, (state) => ({ ...state, searchLookupData: null })),
  on(loadSearchLookupDataSuccess, (state, action) => ({ ...state, searchLookupData: action.payload })),
  on(unloadSearchLookupData, (state) => ({ ...state, searchLookupData: null })),
  on(loadFTPSearchLookupDataSuccess, (state, action) => ({ ...state, searchLookupData: action.payload })),
  on(unloadFTPSearchLookupData, (state) => ({ ...state, searchLookupData: null })),
  on(loadRestrictedAnalytesLookupSuccess, (state, action) => ({ ...state, restrictedAnalytesLookup: action.payload })),
  on(loadSavedSearch, (state, action) => ({ ...state, savedSearch: action.payload })),
  on(loadFTPSavedSearch, (state, action) => ({ ...state, savedSearch: action.search })),

  on(unloadSavedSearch, (state) => ({ ...state, savedSearch: null })),
  on(unloadSavedSearches, (state) => ({ ...state, savedSearches: null })),

  on(setSavedSearchesSortOrder, (state, action) => {
    const newSortOrder = {
      key: action.payload,
      direction: state.savedSearchesSortOrder.direction
    } as SortOrder;

    if (newSortOrder.key === state.savedSearchesSortOrder.key) {
      // same key, toggle sort direction
      newSortOrder.direction = state.savedSearchesSortOrder.direction === ASC ? DESC : ASC;
    } else {
      // different key, sort by key and initial state direction
      newSortOrder.direction = initialState.savedSearchesSortOrder.direction;
    }

    const savedSearches = sortObjects(state.savedSearches, newSortOrder);

    return Object.assign({}, state, {
      savedSearches,
      savedSearchesSortOrder: newSortOrder
    } as ResultsState);
  }),
  on(setFTPSavedSearchesSortOrder, (state, action) => {
    const newSortOrder = {
      key: action.payload,
      direction: state.savedFTPSearchesSortOrder.direction
    } as SortOrder;

    if (newSortOrder.key === state.savedFTPSearchesSortOrder.key) {
      // same key, toggle sort direction
      newSortOrder.direction = state.savedFTPSearchesSortOrder.direction === ASC ? DESC : ASC;
    } else {
      // different key, sort by key and initial state direction
      newSortOrder.direction = initialState.savedFTPSearchesSortOrder.direction;
    }

    const savedSearches = sortObjects(state.savedSearches, newSortOrder);

    return Object.assign({}, state, {
      savedSearches,
      savedFTPSearchesSortOrder: newSortOrder
    } as ResultsState);
  }),
  on(setSearchResultsSortOrder, (state, action) => {
    const newSortOrder = {
      key: action.payload,
      direction: state.searchResultsSortOrder.direction
    } as SortOrder;

    if (newSortOrder.key === state.searchResultsSortOrder.key) {
      // same key, toggle sort direction
      newSortOrder.direction = state.searchResultsSortOrder.direction === ASC ? DESC : ASC;
    } else {
      // different key, sort by key and initial state direction
      newSortOrder.direction = initialState.searchResultsSortOrder.direction;
    }

    const searchResults = sortObjects(state.searchResults, newSortOrder);

    return Object.assign({}, state, {
      searchResults,
      searchResultsSortOrder: newSortOrder
    } as ResultsState);
  }),
  on(onScheduleSavedSearchSuccess, (state, action) => ({ ...state, savedSearch: action.search }))
);

export const selectResultsState = createFeatureSelector<ResultsState>('resultsState');

